import colors from '@/theme/colors';
import { TContentDomain, TContentStatus, TContentType, TContentVisibility } from '@/types/Content.types';
import {
  Ban,
  CheckCircle2,
  FileText,
  type LucideIcon,
  Newspaper,
  PencilLine,
  Undo2,
  Video,
} from 'lucide-react';

export const STATUS_COLOR: Record<TContentStatus, string> = {
  draft: colors.extra.orange,
  published: colors.extra.teal,
  removed: colors.extra.red,
  withdrawn: colors.extra.violet,
};

export const STATUS_LABEL: Record<TContentStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  removed: 'Removed',
  withdrawn: 'Withdrawn',
};

// A glyph per status so an admin-removed item and a creator-withdrawn one are
// told apart at a glance, not by reading the word.
export const STATUS_ICON: Record<TContentStatus, LucideIcon> = {
  draft: PencilLine,
  published: CheckCircle2,
  removed: Ban,
  withdrawn: Undo2,
};

export const TYPE_LABEL: Record<TContentType, string> = {
  video: 'Reel',
  article: 'Article',
  post: 'Post',
};

export const TYPE_ICON: Record<TContentType, LucideIcon> = {
  video: Video,
  article: Newspaper,
  post: FileText,
};

export const TYPE_ICON_COLOR: Record<TContentType, string> = {
  video: colors.secondary[500],
  article: colors.primary[500],
  post: colors.extra.waterBlue,
};

export const DOMAIN_LABEL: Record<TContentDomain, string> = {
  mental: 'Mental',
  spiritual: 'Spiritual',
  vocational: 'Vocational',
  fun: 'Fun',
  purpose: 'Purpose',
  social: 'Social',
  physical: 'Physical',
};

export const getContentTypeLabel = (type: TContentType): string => TYPE_LABEL[type];

export const getContentStatusLabel = (status?: TContentStatus | null): string => (status ? STATUS_LABEL[status] : '/');

export const getContentDomainLabel = (domain?: TContentDomain | null): string => (domain ? DOMAIN_LABEL[domain] : '/');

// Comma-joined domain labels for the (now multiple) content domains.
export const getContentDomainsLabel = (domains?: TContentDomain[] | null): string =>
  domains?.length ? domains.map((domain) => DOMAIN_LABEL[domain]).join(', ') : '/';

export const getContentVisibilityLabel = (visibility?: TContentVisibility | null): string => {
  if (!visibility) return '/';
  if (visibility.public) return 'Public';
  if (visibility.orgIds?.length) return 'Organization';
  return 'Private';
};
